import getImageUrl, { type ImageFit } from './getImageUrl'

import type { PublicImage } from '@/service/__generated-api'
import type { BoardLayout, Widget, WidgetDto } from '@/types'

function getImageSize(layout: BoardLayout, withTextContent = false) {
  switch (layout) {
    case 'square': {
      return withTextContent
        ? { width: 1957, height: 911 }
        : { width: 1957, height: 1957 }
    }
    case 'framed': {
      return withTextContent
        ? { width: 1957, height: 1829 }
        : { width: 1957, height: 2824 }
    }
    case 'fullscreen': {
      return { width: 2160, height: 3840 }
    }
  }
}

function getParsedImage({
  image,
  imageFit = 'fill',
  size
}: {
  image?: PublicImage
  size?: { width: number; height: number }
  imageFit?: ImageFit
}): HTMLImageElement {
  const img = new Image()
  if (!image) return img

  const imgSize = size ?? { width: image.width ?? 0, height: image.height ?? 0 }
  const file = image.file
  const expiresAt = file ? new URL(file).searchParams.get('Expires') : null
  const url =
    (file &&
      getImageUrl({
        file,
        ...imgSize,
        ppoi: image.ppoi_tuple as unknown as [string, string],
        imageFit
      })) ||
    ''
  img.src = url
  if (expiresAt) img.dataset.expires = expiresAt

  return img
}

export default function parseImages(
  widget: WidgetDto,
  layout: BoardLayout = 'square'
): Widget {
  switch (widget?.resourcetype) {
    case 'PosterWidget': {
      return {
        ...widget,
        image: getParsedImage({
          image: widget.image,
          size: getImageSize(layout)
        })
      }
    }
    case 'StudentArtGalleryWidget': {
      return {
        ...widget,
        arts: widget.arts.map(art => ({
          ...art,
          image: getParsedImage({
            image: art.image,
            imageFit: 'fit'
          })
        }))
      }
    }
    case 'StudentsPicWidget': {
      return {
        ...widget,
        pics: widget.pics.map(pic => ({
          ...pic,
          image: getParsedImage({
            image: pic.image,
            size: getImageSize(layout)
          })
        }))
      }
    }
    case 'JournalWidget': {
      return {
        ...widget,
        journal_items: widget.journal_items.map(article => ({
          ...article,
          photo: getParsedImage({
            image: article.photo,
            size: getImageSize(layout, true)
          })
        }))
      }
    }
    case 'EventInfoWithPhotoWidget': {
      return {
        ...widget,
        photo: getParsedImage({
          image: widget.photo,
          size: getImageSize(layout, true)
        })
      }
    }
    case 'VariantManualCongratulationsWidget': {
      return {
        ...widget,
        items: widget.items.map(item => ({
          ...item,
          images: item.images.map(image =>
            getParsedImage({
              image,
              size: getImageSize(layout),
              imageFit: 'fit'
            })
          )
        }))
      }
    }
    default:
      return widget
  }
}
